<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import ScheduledMessageAPI from 'dashboard/api/scheduledMessages';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const { t } = useI18n();

// `processing` (message grabbed by the cron, mid-delivery) has no key of its
// own in the i18n table for this dialog — it's a very short-lived state, so
// it falls back to the "Scheduled" label, the closest non-final bucket.
const STATUS_KEYS = {
  pending: 'PENDING',
  processing: 'PENDING',
  sent: 'SENT',
  failed: 'FAILED',
  canceled: 'CANCELED',
};

const dialogRef = ref(null);
const isSaving = ref(false);
const showErrors = ref(false);
const isLoadingList = ref(false);
const cancelingId = ref(null);
const deal = ref(null);
const scheduledMessages = ref([]);

const form = reactive({
  content: '',
  scheduledAt: '',
});

const hasConversation = computed(() => !!deal.value?.conversationId);

const isDateInvalid = computed(() => {
  if (!showErrors.value) return false;
  if (!form.scheduledAt) return true;
  return new Date(form.scheduledAt) <= new Date();
});

const resetForm = () => {
  form.content = '';
  form.scheduledAt = '';
  showErrors.value = false;
};

const statusLabel = status =>
  t(`CRM_FUNIL.SCHEDULE.STATUS.${STATUS_KEYS[status] || 'PENDING'}`);

const formatDateTime = isoString => new Date(isoString).toLocaleString();

const loadList = async () => {
  if (!hasConversation.value) {
    scheduledMessages.value = [];
    return;
  }

  isLoadingList.value = true;
  try {
    const { data } = await ScheduledMessageAPI.getForConversation(
      deal.value.conversationId
    );
    scheduledMessages.value = data?.payload || [];
  } catch {
    scheduledMessages.value = [];
  } finally {
    isLoadingList.value = false;
  }
};

const open = openedDeal => {
  deal.value = openedDeal;
  resetForm();
  dialogRef.value?.open();
  loadList();
};

const handleConfirm = async () => {
  if (isSaving.value || !hasConversation.value) return;

  showErrors.value = true;
  if (!form.content.trim() || isDateInvalid.value) return;

  isSaving.value = true;
  try {
    await ScheduledMessageAPI.create({
      content: form.content.trim(),
      scheduled_at: new Date(form.scheduledAt).toISOString(),
      conversation_id: deal.value.conversationId,
      deal_id: deal.value.id,
    });
    useAlert(t('CRM_FUNIL.SCHEDULE.SUCCESS'));
    resetForm();
    await loadList();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CRM_FUNIL.SCHEDULE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleCancelOne = async message => {
  if (cancelingId.value) return;

  cancelingId.value = message.id;
  try {
    await ScheduledMessageAPI.delete(message.id);
    useAlert(t('CRM_FUNIL.SCHEDULE.STATUS.CANCELED'));
    await loadList();
  } catch {
    useAlert(t('CRM_FUNIL.SCHEDULE.ERROR'));
  } finally {
    cancelingId.value = null;
  }
};

defineExpose({
  open,
  form,
  scheduledMessages,
  handleConfirm,
  handleCancelOne,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :show-confirm-button="hasConversation"
    :confirm-button-label="t('CRM_FUNIL.SCHEDULE.SUBMIT')"
    :is-loading="isSaving"
    :disable-confirm-button="isSaving"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CRM_FUNIL.SCHEDULE.TITLE') }}
      </span>

      <p v-if="!hasConversation" class="text-sm text-n-amber-11">
        {{ t('CRM_FUNIL.SCHEDULE.NO_CONVERSATION') }}
      </p>

      <template v-else>
        <TextArea
          v-model="form.content"
          :label="t('CRM_FUNIL.SCHEDULE.MESSAGE')"
          :disabled="isSaving"
        />

        <Input
          v-model="form.scheduledAt"
          type="datetime-local"
          :label="t('CRM_FUNIL.SCHEDULE.WHEN')"
          :message="isDateInvalid ? t('CRM_FUNIL.SCHEDULE.PAST') : ''"
          :message-type="isDateInvalid ? 'error' : 'info'"
          :disabled="isSaving"
        />

        <div class="flex flex-col gap-2">
          <p
            v-if="!isLoadingList && !scheduledMessages.length"
            class="text-xs text-n-slate-10"
          >
            {{ t('CRM_FUNIL.SCHEDULE.EMPTY') }}
          </p>
          <div
            v-for="message in scheduledMessages"
            :key="message.id"
            class="flex items-center justify-between gap-2 p-2 border rounded-lg border-n-weak"
          >
            <div class="flex flex-col min-w-0 gap-0.5">
              <div class="flex items-center gap-2 text-xs">
                <span class="font-medium text-n-slate-12">
                  {{ formatDateTime(message.scheduled_at) }}
                </span>
                <span class="text-n-slate-10">
                  {{ statusLabel(message.status) }}
                </span>
              </div>
              <p class="text-xs truncate text-n-slate-11">
                {{ message.content }}
              </p>
            </div>
            <Button
              v-if="message.status === 'pending'"
              :label="t('CRM_FUNIL.SCHEDULE.CANCEL_ONE')"
              variant="faded"
              color="ruby"
              size="sm"
              type="button"
              :disabled="cancelingId === message.id"
              @click="handleCancelOne(message)"
            />
          </div>
        </div>
      </template>
    </div>
  </Dialog>
</template>
